// The Job board (E, at the board): the settlement's recorded asks. Two
// kinds only — supply (bring N units of a class, settled at the board)
// and charter (carry the client to a named place, settled by the trip).
// The Provisions Office keeps it warm; players post on the same rails.
//
// No distance to any destination is ever printed (trades.md §3): a
// charter names a place in words, and finding it is the work.

import { escapeHtml } from "./escape";

interface BoardJob {
  jobId: string;
  kind: string;
  clientName: string;
  ask: string;
  pays: string;
  status: string;
  mine: boolean;
  acceptedByMe: boolean;
  npcSeeded: boolean;
  wantClassId: string | null;
  wantUnits: number | null;
}

interface JobsResponse {
  jobs: BoardJob[];
  classes: Array<{ id: string; publicName: string }>;
  landmarks: Array<{ id: string; label: string }>;
}

interface HoldStack {
  location: string;
  spawnId: string;
  name: string;
  className: string;
  units: number;
}

export class JobsPanel {
  private root: HTMLDivElement;
  private body: HTMLDivElement;
  private open = false;
  private board: JobsResponse | undefined;
  private hold: HoldStack[] = [];

  constructor(
    parent: HTMLElement,
    private actions: {
      postSupply: (
        wantClassId: string,
        wantUnits: number,
        rewardSpawnId: string,
        rewardUnits: number,
      ) => void;
      postCharter: (
        destinationLandmarkId: string,
        rewardSpawnId: string,
        rewardUnits: number,
      ) => void;
      accept: (jobId: string) => void;
      deliver: (jobId: string, spawnId: string) => void;
      cancel: (jobId: string) => void;
    },
  ) {
    this.root = document.createElement("div");
    this.root.className = "panel-float jobs-panel";
    this.root.innerHTML = `
      <div class="panel-title">THE JOB BOARD <span class="panel-hint">E to close</span></div>
      <div class="jobs-body"></div>
    `;
    this.root.style.display = "none";
    parent.append(this.root);
    this.body = this.root.querySelector(".jobs-body") as HTMLDivElement;
  }

  isOpen(): boolean {
    return this.open;
  }

  toggle(): void {
    this.open = !this.open;
    this.root.style.display = this.open ? "" : "none";
    if (this.open) void this.refresh();
  }

  close(): void {
    this.open = false;
    this.root.style.display = "none";
  }

  refreshIfOpen(): void {
    if (this.open) void this.refresh();
  }

  private async refresh(): Promise<void> {
    this.body.innerHTML = `<div class="panel-empty">reading the board…</div>`;
    try {
      const [jobsRes, invRes] = await Promise.all([fetch("/api/jobs"), fetch("/api/inventory")]);
      if (!jobsRes.ok) {
        const err = (await jobsRes.json().catch(() => ({}))) as { error?: string };
        this.body.innerHTML = `<div class="panel-empty">${escapeHtml(err.error ?? "the board is dark")}</div>`;
        return;
      }
      this.board = (await jobsRes.json()) as JobsResponse;
      const inv = invRes.ok ? ((await invRes.json()) as { stacks?: HoldStack[] }) : {};
      this.hold = (inv.stacks ?? []).filter((s) => s.location === "hold" && s.units > 0);
      this.render();
    } catch {
      this.body.innerHTML = `<div class="panel-empty">the board is dark (try again)</div>`;
    }
  }

  private render(): void {
    const board = this.board;
    if (!board) return;
    const jobs = board.jobs.length
      ? board.jobs.map((job) => this.jobRow(job)).join("")
      : `<div class="panel-empty">Nothing is asked of anybody today.</div>`;
    this.body.innerHTML = `<div class="jobs-list">${jobs}</div>${this.postForms(board)}`;
    this.wire();
  }

  private jobRow(job: BoardJob): string {
    let action = "";
    if (job.mine) {
      action = `<button class="jobs-cancel" data-job="${escapeHtml(job.jobId)}">take it down</button>`;
    } else if (job.kind === "supply" && job.status === "offered") {
      const payable = this.hold.filter(
        (s) =>
          job.wantClassId !== null && s.units >= (job.wantUnits ?? 0) && this.classMatches(s, job),
      );
      action = payable.length
        ? `<select class="jobs-pay" data-job="${escapeHtml(job.jobId)}">
             ${payable
               .map(
                 (s) =>
                   `<option value="${escapeHtml(s.spawnId)}">deliver ${escapeHtml(s.name)}</option>`,
               )
               .join("")}
           </select>
           <button class="jobs-deliver" data-job="${escapeHtml(job.jobId)}">deliver</button>`
        : `<span class="jobs-note">you are not carrying what it asks</span>`;
    } else if (job.kind === "charter" && job.status === "offered") {
      action = `<button class="jobs-accept" data-job="${escapeHtml(job.jobId)}">take the charter</button>`;
    } else if (job.acceptedByMe) {
      action = `<span class="jobs-note">yours — fly it</span>
        <button class="jobs-cancel" data-job="${escapeHtml(job.jobId)}">hand it back</button>`;
    } else if (job.status === "accepted") {
      action = `<span class="jobs-note">taken</span>`;
    }
    const poster = job.npcSeeded
      ? `${escapeHtml(job.clientName)} <em>(civic)</em>`
      : escapeHtml(job.clientName);
    return `
      <div class="jobs-row">
        <div><b>${escapeHtml(job.ask)}</b> — ${escapeHtml(job.pays)}</div>
        <div class="jobs-row-sub"><span>${poster}</span>${action}</div>
      </div>`;
  }

  private classMatches(stack: HoldStack, job: BoardJob): boolean {
    // The board serves class ids; the hold serves class display names.
    // The server re-verifies by id — this only filters the dropdown.
    const wanted = this.board?.classes.find((c) => c.id === job.wantClassId);
    return wanted !== undefined && wanted.publicName === stack.className;
  }

  private postForms(board: JobsResponse): string {
    if (this.hold.length === 0) {
      return `<div class="jobs-note">Posting needs pay in your hold — the board escrows it up front.</div>`;
    }
    const rewardOptions = this.hold
      .map(
        (s) =>
          `<option value="${escapeHtml(s.spawnId)}">${escapeHtml(s.name)} (${s.units} carried)</option>`,
      )
      .join("");
    return `
      <div class="jobs-post">
        <div class="jobs-post-title">POST AN ASK — SUPPLY</div>
        <span class="jobs-dim">bring</span>
        <input class="jobs-want-units" type="number" min="1" placeholder="units" />
        <select class="jobs-want-class">
          ${board.classes
            .map((c) => `<option value="${escapeHtml(c.id)}">${escapeHtml(c.publicName)}</option>`)
            .join("")}
        </select>
        <span class="jobs-dim">pay</span>
        <input class="jobs-supply-reward-units" type="number" min="1" placeholder="units" />
        <select class="jobs-supply-reward-spawn">${rewardOptions}</select>
        <button class="jobs-post-supply">post it</button>
      </div>
      <div class="jobs-post">
        <div class="jobs-post-title">POST AN ASK — CHARTER</div>
        <span class="jobs-dim">carry me to</span>
        <select class="jobs-charter-dest">
          ${board.landmarks
            .map((l) => `<option value="${escapeHtml(l.id)}">${escapeHtml(l.label)}</option>`)
            .join("")}
        </select>
        <span class="jobs-dim">pay</span>
        <input class="jobs-charter-reward-units" type="number" min="1" placeholder="units" />
        <select class="jobs-charter-reward-spawn">${rewardOptions}</select>
        <button class="jobs-post-charter">post it</button>
      </div>`;
  }

  private wire(): void {
    for (const btn of this.body.querySelectorAll<HTMLButtonElement>(".jobs-cancel")) {
      btn.addEventListener("click", () => {
        if (btn.dataset.job) this.actions.cancel(btn.dataset.job);
      });
    }
    for (const btn of this.body.querySelectorAll<HTMLButtonElement>(".jobs-accept")) {
      btn.addEventListener("click", () => {
        if (btn.dataset.job) this.actions.accept(btn.dataset.job);
      });
    }
    for (const btn of this.body.querySelectorAll<HTMLButtonElement>(".jobs-deliver")) {
      btn.addEventListener("click", () => {
        const jobId = btn.dataset.job ?? "";
        const pay = this.body.querySelector<HTMLSelectElement>(
          `.jobs-pay[data-job="${CSS.escape(jobId)}"]`,
        );
        if (jobId && pay?.value) this.actions.deliver(jobId, pay.value);
      });
    }
    this.body
      .querySelector<HTMLButtonElement>(".jobs-post-supply")
      ?.addEventListener("click", () => {
        const wantUnits = Number(
          this.body.querySelector<HTMLInputElement>(".jobs-want-units")?.value,
        );
        const wantClass =
          this.body.querySelector<HTMLSelectElement>(".jobs-want-class")?.value ?? "";
        const rewardUnits = Number(
          this.body.querySelector<HTMLInputElement>(".jobs-supply-reward-units")?.value,
        );
        const rewardSpawn =
          this.body.querySelector<HTMLSelectElement>(".jobs-supply-reward-spawn")?.value ?? "";
        if (wantClass && rewardSpawn && wantUnits > 0 && rewardUnits > 0) {
          this.actions.postSupply(wantClass, wantUnits, rewardSpawn, rewardUnits);
        }
      });
    this.body
      .querySelector<HTMLButtonElement>(".jobs-post-charter")
      ?.addEventListener("click", () => {
        const dest = this.body.querySelector<HTMLSelectElement>(".jobs-charter-dest")?.value ?? "";
        const rewardUnits = Number(
          this.body.querySelector<HTMLInputElement>(".jobs-charter-reward-units")?.value,
        );
        const rewardSpawn =
          this.body.querySelector<HTMLSelectElement>(".jobs-charter-reward-spawn")?.value ?? "";
        if (dest && rewardSpawn && rewardUnits > 0) {
          this.actions.postCharter(dest, rewardSpawn, rewardUnits);
        }
      });
  }
}
